import React, { useState, useEffect } from 'react';
import { useTaxonomy } from '../../hooks/useTaxonomy';

/**
 * Unified Listing Filter Component
 * Used across Universities, Programs, and Scholarships pages
 * 
 * @param {string} context - 'universities' | 'programs' | 'scholarships'
 * @param {object} filters - Current filter state
 * @param {function} onFilterChange - Callback when filters change
 * @param {number} resultCount - Number of results matching current filters
 */
export const ListingFilters = ({ context, filters, onFilterChange, resultCount }) => {
  const { data: taxonomyData, loading: taxonomyLoading } = useTaxonomy();
  const [isMinimized, setIsMinimized] = useState(false);
  const [showAllFields, setShowAllFields] = useState(false);
  const [showAllSubjects, setShowAllSubjects] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);

  // Extract taxonomy by type
  const levelOptions = taxonomyData.filter(t => t.type === 'level_of_study');
  const fieldOptions = taxonomyData.filter(t => t.type === 'field_of_study');
  const subjectOptions = taxonomyData.filter(t => t.type === 'subject');
  const modeOptions = taxonomyData.filter(t => t.type === 'mode_of_study');
  const intakeOptions = taxonomyData.filter(t => t.type === 'intake');
  const institutionTypeOptions = taxonomyData.filter(t => t.type === 'institution_type');
  const scholarshipTypeOptions = taxonomyData.filter(t => t.type === 'scholarship_type');

  // Filter subjects by selected field (if parent_id matches field)
  const filteredSubjects = filters.selectedField
    ? subjectOptions.filter(sub => {
        const field = fieldOptions.find(f => f.label === filters.selectedField);
        return field ? sub.parent_id === field.id : true;
      })
    : subjectOptions;

  // Scroll behavior for sticky filter
  useEffect(() => {
    const handleScroll = () => {
      setIsMinimized(window.scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const displayedFields = showAllFields ? fieldOptions : fieldOptions.slice(0, 5);
  const displayedSubjects = showAllSubjects ? filteredSubjects : filteredSubjects.slice(0, 6);

  const hasActiveFilters = 
    filters.selectedField || 
    (filters.selectedSubjects && filters.selectedSubjects.length > 0) ||
    filters.searchQuery ||
    filters.selectedCountry ||
    filters.selectedLevel ||
    filters.selectedMode ||
    filters.selectedInstitutionType ||
    filters.selectedScholarshipType;

  const handleClearAll = () => {
    onFilterChange({
      selectedField: '',
      selectedSubjects: [],
      searchQuery: '',
      selectedCountry: context === 'universities' ? 'Malaysia' : '',
      selectedState: '',
      selectedLevel: '',
      selectedMode: '',
      selectedInstitutionType: '',
      selectedScholarshipType: '',
      showCashbackOnly: false,
      showScholarshipOnly: false
    });
  };

  const handleFieldSelect = (fieldLabel) => {
    onFilterChange({
      ...filters,
      selectedField: filters.selectedField === fieldLabel ? '' : fieldLabel,
      selectedSubjects: []
    });
  };

  const handleSubjectToggle = (subjectLabel) => {
    const currentSubjects = filters.selectedSubjects || [];
    onFilterChange({
      ...filters,
      selectedSubjects: currentSubjects.includes(subjectLabel)
        ? currentSubjects.filter(s => s !== subjectLabel)
        : [...currentSubjects, subjectLabel]
    });
  };

  if (taxonomyLoading) {
    return (
      <div className="bg-white border-2 border-[#E5E7EB] rounded-2xl p-6 text-center">
        <div className="inline-block w-6 h-6 border-3 border-[#F97316] border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-2 text-sm text-[#6B7280]">Loading filters...</p>
      </div>
    );
  }

  // ========== MINIMIZED STICKY FILTER ==========
  if (isMinimized) {
    return (
      <section className="fixed top-16 left-0 right-0 z-30 bg-white border-b-2 border-[#E5E7EB] shadow-md transition-all duration-300">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Field of Study Filter (ALWAYS VISIBLE) */}
            <div className="flex items-center gap-3 flex-1">
              <svg className="w-4 h-4 text-[#F97316] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              
              {/* Field of Study Dropdown - ALWAYS VISIBLE */}
              <select
                value={filters.selectedField || ''}
                onChange={(e) => handleFieldSelect(e.target.value)}
                className="px-3 py-1.5 border-2 border-[#E5E7EB] rounded-lg text-sm bg-white focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316] transition-colors"
              >
                <option value="">All Fields</option>
                {fieldOptions.map(field => (
                  <option key={field.id} value={field.label}>{field.label}</option>
                ))}
              </select>

              {/* Active Filters Count */}
              {filters.selectedField && (
                <span className="px-2 py-1 bg-[#F97316] text-white text-xs font-medium rounded-md flex items-center gap-1.5">
                  {filters.selectedField}
                  <button 
                    onClick={() => handleFieldSelect(filters.selectedField)} 
                    className="hover:bg-white/20 rounded-full p-0.5"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              )}
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-3 py-1.5 bg-[#F8FAFC] border border-[#E5E7EB] text-[#4B5563] hover:border-[#F97316] rounded-lg text-xs font-medium transition-all"
              >
                Expand Filters
              </button>
              {hasActiveFilters && (
                <button
                  onClick={handleClearAll}
                  className="px-3 py-1.5 bg-white border-2 border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white rounded-lg text-xs font-medium transition-all"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ========== FULL FILTER PANEL ==========
  return (
    <>
      <section className="py-6 bg-white border-2 border-[#E5E7EB] rounded-2xl mt-4 transition-all duration-300 shadow-sm">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="bg-gradient-to-br from-[#F8FAFC] via-white to-[#F8FAFC] rounded-2xl border-2 border-[#E5E7EB] p-4 md:p-5 shadow-lg">
            
            {/* SECTION 1: Filter by Field */}
            <div className="mb-3 pb-3 border-b border-[#E5E7EB]">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm md:text-base font-bold text-[#111827]">Filter by Field</h2>
                {hasActiveFilters && (
                  <button
                    onClick={handleClearAll}
                    className="px-3 py-1.5 bg-white border-2 border-[#E5E7EB] text-[#4B5563] hover:border-[#F97316] hover:text-[#F97316] rounded-lg text-xs font-medium transition-all flex items-center gap-1.5"
                    data-testid="reset-filter-button"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reset
                  </button>
                )}
              </div>
              
              {/* Desktop: Buttons */}
              <div className="hidden md:flex flex-wrap gap-2">
                {displayedFields.map(field => (
                  <button
                    key={field.id}
                    onClick={() => handleFieldSelect(field.label)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 ${
                      filters.selectedField === field.label
                        ? 'bg-gradient-to-r from-[#F97316] to-[#fb7e2e] text-white shadow-md'
                        : 'bg-white text-[#4B5563] hover:bg-[#F8FAFC] border-2 border-[#E5E7EB] hover:border-[#F97316]'
                    }`}
                  >
                    {field.label}
                  </button>
                ))}
                {fieldOptions.length > 5 && (
                  <button
                    onClick={() => setShowAllFields(!showAllFields)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#F97316] hover:bg-[#FEF3C7] border-2 border-[#F97316] transition-all"
                  >
                    {showAllFields ? 'Less' : `+${fieldOptions.length - 5}`}
                  </button>
                )}
              </div>

              {/* Mobile: Dropdown */}
              <div className="md:hidden">
                <select
                  value={filters.selectedField || ''}
                  onChange={(e) => handleFieldSelect(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-sm bg-white"
                >
                  <option value="">All Fields</option>
                  {fieldOptions.map(field => (
                    <option key={field.id} value={field.label}>{field.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* SECTION 2: Refine by Subject (Contextual) */}
            {filters.selectedField && filteredSubjects.length > 0 && (
              <div className="mb-3 pb-3 border-b border-[#E5E7EB]">
                <h3 className="text-sm font-bold text-[#111827] mb-2">Refine by Subject</h3>
                
                {/* Desktop: Buttons */}
                <div className="hidden md:flex flex-wrap gap-2">
                  {displayedSubjects.map(subject => (
                    <button
                      key={subject.id}
                      onClick={() => handleSubjectToggle(subject.label)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                        (filters.selectedSubjects || []).includes(subject.label)
                          ? 'bg-[#F97316] text-white shadow-md'
                          : 'bg-white text-[#4B5563] hover:bg-[#F8FAFC] border-2 border-[#E5E7EB] hover:border-[#F97316]'
                      }`}
                    >
                      {subject.label}
                    </button>
                  ))}
                  {filteredSubjects.length > 6 && (
                    <button
                      onClick={() => setShowAllSubjects(!showAllSubjects)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#F97316] hover:bg-[#FEF3C7] border-2 border-[#F97316] transition-all"
                    >
                      {showAllSubjects ? 'Less' : `+${filteredSubjects.length - 6}`}
                    </button>
                  )}
                </div>

                {/* Mobile: Multi-select */}
                <div className="md:hidden">
                  <div className="flex flex-wrap gap-2">
                    {filteredSubjects.map(subject => (
                      <button
                        key={subject.id}
                        onClick={() => handleSubjectToggle(subject.label)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          (filters.selectedSubjects || []).includes(subject.label)
                            ? 'bg-[#F97316] text-white'
                            : 'bg-white text-[#4B5563] border border-[#E5E7EB]'
                        }`}
                      >
                        {subject.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* SECTION 3: Context-Specific Advanced Filters */}
            <div>
              {/* Mobile: Filter Button */}
              <div className="md:hidden">
                <button
                  onClick={() => setShowMobileModal(true)}
                  className="w-full px-4 py-2.5 bg-white border-2 border-[#E5E7EB] text-[#111827] font-medium rounded-xl flex items-center justify-between hover:border-[#F97316] transition-all"
                  data-testid="mobile-filter-button"
                >
                  <span className="flex items-center gap-2 text-sm">
                    <svg className="w-4 h-4 text-[#F97316]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    More Filters
                  </span>
                  <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Desktop: Advanced Filter Bar */}
              <div className="hidden md:block">
                <div className="grid grid-cols-12 gap-3 mb-3">
                  {/* Search */}
                  <div className="col-span-5">
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder={
                          context === 'universities' ? 'Search universities...' :
                          context === 'programs' ? 'Search programs...' :
                          'Search scholarships...'
                        }
                        value={filters.searchQuery || ''}
                        onChange={(e) => onFilterChange({ ...filters, searchQuery: e.target.value })}
                        className="w-full pl-10 pr-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-transparent text-xs bg-white"
                        data-testid="search-input"
                      />
                    </div>
                  </div>

                  {/* Context-specific filters */}
                  {context === 'universities' && (
                    <>
                      <div className="col-span-2">
                        <select
                          value={filters.selectedInstitutionType || ''}
                          onChange={(e) => onFilterChange({ ...filters, selectedInstitutionType: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-xs bg-white"
                        >
                          <option value="">Institution Type</option>
                          {institutionTypeOptions.map(type => (
                            <option key={type.id} value={type.label}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <select
                          value={filters.selectedLevel || ''}
                          onChange={(e) => onFilterChange({ ...filters, selectedLevel: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-xs bg-white"
                        >
                          <option value="">Level</option>
                          {levelOptions.map(level => (
                            <option key={level.id} value={level.label}>{level.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <select
                          value={filters.selectedCountry || ''}
                          onChange={(e) => onFilterChange({ ...filters, selectedCountry: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-xs bg-white"
                        >
                          <option value="">Country</option>
                          <option value="Malaysia">Malaysia</option>
                          <option value="USA">USA</option>
                          <option value="UK">UK</option>
                          <option value="Singapore">Singapore</option>
                        </select>
                      </div>
                    </>
                  )}

                  {context === 'programs' && (
                    <>
                      <div className="col-span-2">
                        <select
                          value={filters.selectedLevel || ''}
                          onChange={(e) => onFilterChange({ ...filters, selectedLevel: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-xs bg-white"
                        >
                          <option value="">Level</option>
                          {levelOptions.map(level => (
                            <option key={level.id} value={level.label}>{level.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <select
                          value={filters.selectedMode || ''}
                          onChange={(e) => onFilterChange({ ...filters, selectedMode: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-xs bg-white"
                        >
                          <option value="">Mode</option>
                          {modeOptions.map(mode => (
                            <option key={mode.id} value={mode.label}>{mode.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <select
                          value={filters.selectedCountry || ''}
                          onChange={(e) => onFilterChange({ ...filters, selectedCountry: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-xs bg-white"
                        >
                          <option value="">Country</option>
                          <option value="Malaysia">Malaysia</option>
                          <option value="USA">USA</option>
                          <option value="UK">UK</option>
                          <option value="Singapore">Singapore</option>
                        </select>
                      </div>
                    </>
                  )}

                  {context === 'scholarships' && (
                    <>
                      <div className="col-span-3">
                        <select
                          value={filters.selectedLevel || ''}
                          onChange={(e) => onFilterChange({ ...filters, selectedLevel: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-xs bg-white"
                        >
                          <option value="">Level</option>
                          {levelOptions.map(level => (
                            <option key={level.id} value={level.label}>{level.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-4">
                        <select
                          value={filters.selectedScholarshipType || ''}
                          onChange={(e) => onFilterChange({ ...filters, selectedScholarshipType: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-xs bg-white"
                        >
                          <option value="">Scholarship Type</option>
                          {scholarshipTypeOptions.map(type => (
                            <option key={type.id} value={type.label}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>

                {/* Result count */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {context === 'programs' && (
                      <>
                        <label className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={filters.showCashbackOnly || false}
                            onChange={(e) => onFilterChange({ ...filters, showCashbackOnly: e.target.checked })}
                            className="w-4 h-4 text-[#F97316] border-2 border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#F97316]"
                          />
                          <span className="text-xs text-[#4B5563] group-hover:text-[#111827] transition-colors font-medium">Cashback</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={filters.showScholarshipOnly || false}
                            onChange={(e) => onFilterChange({ ...filters, showScholarshipOnly: e.target.checked })}
                            className="w-4 h-4 text-[#F97316] border-2 border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#F97316]"
                          />
                          <span className="text-xs text-[#4B5563] group-hover:text-[#111827] transition-colors font-medium">Scholarship</span>
                        </label>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-[#6B7280]">
                    <span className="font-semibold text-[#111827]">{resultCount}</span> results found
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Filter Modal */}
      {showMobileModal && (
        <MobileFilterModal
          context={context}
          filters={filters}
          onFilterChange={onFilterChange}
          onClose={() => setShowMobileModal(false)}
          levelOptions={levelOptions}
          modeOptions={modeOptions}
          institutionTypeOptions={institutionTypeOptions}
          scholarshipTypeOptions={scholarshipTypeOptions}
          handleClearAll={handleClearAll}
        />
      )}
    </>
  );
};

// Mobile Filter Modal Component
const MobileFilterModal = ({ 
  context, 
  filters, 
  onFilterChange, 
  onClose, 
  levelOptions, 
  modeOptions, 
  institutionTypeOptions,
  scholarshipTypeOptions,
  handleClearAll 
}) => {
  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end md:hidden" 
      onClick={onClose}
    >
      <div 
        className="bg-white w-full rounded-t-3xl max-h-[85vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-[#111827]">More Filters</h3>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F8FAFC] text-[#6B7280] hover:bg-[#E5E7EB] transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-5 space-y-4">
          {/* Search */}
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-2">Search</label>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search..."
                value={filters.searchQuery || ''}
                onChange={(e) => onFilterChange({ ...filters, searchQuery: e.target.value })}
                className="w-full pl-10 pr-3 py-2.5 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-sm"
              />
            </div>
          </div>

          {/* Level */}
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-2">Level</label>
            <select
              value={filters.selectedLevel || ''}
              onChange={(e) => onFilterChange({ ...filters, selectedLevel: e.target.value })}
              className="w-full px-3 py-2.5 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-sm"
            >
              <option value="">All Levels</option>
              {levelOptions.map(level => (
                <option key={level.id} value={level.label}>{level.label}</option>
              ))}
            </select>
          </div>

          {/* Context-specific filters */}
          {context === 'universities' && (
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-2">Institution Type</label>
              <select
                value={filters.selectedInstitutionType || ''}
                onChange={(e) => onFilterChange({ ...filters, selectedInstitutionType: e.target.value })}
                className="w-full px-3 py-2.5 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-sm"
              >
                <option value="">All Types</option>
                {institutionTypeOptions.map(type => (
                  <option key={type.id} value={type.label}>{type.label}</option>
                ))}
              </select>
            </div>
          )}

          {context === 'programs' && (
            <>
              <div>
                <label className="block text-xs font-semibold text-[#111827] mb-2">Mode</label>
                <select
                  value={filters.selectedMode || ''}
                  onChange={(e) => onFilterChange({ ...filters, selectedMode: e.target.value })}
                  className="w-full px-3 py-2.5 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-sm"
                >
                  <option value="">All Modes</option>
                  {modeOptions.map(mode => (
                    <option key={mode.id} value={mode.label}>{mode.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-3 pt-2">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.showCashbackOnly || false}
                    onChange={(e) => onFilterChange({ ...filters, showCashbackOnly: e.target.checked })}
                    className="w-4 h-4 text-[#F97316] border-2 border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#F97316]"
                  />
                  <span className="text-sm text-[#4B5563] font-medium">Cashback</span>
                </label>
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.showScholarshipOnly || false}
                    onChange={(e) => onFilterChange({ ...filters, showScholarshipOnly: e.target.checked })}
                    className="w-4 h-4 text-[#F97316] border-2 border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#F97316]"
                  />
                  <span className="text-sm text-[#4B5563] font-medium">Scholarship</span>
                </label>
              </div>
            </>
          )}

          {context === 'scholarships' && (
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-2">Scholarship Type</label>
              <select
                value={filters.selectedScholarshipType || ''}
                onChange={(e) => onFilterChange({ ...filters, selectedScholarshipType: e.target.value })}
                className="w-full px-3 py-2.5 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-sm"
              >
                <option value="">All Types</option>
                {scholarshipTypeOptions.map(type => (
                  <option key={type.id} value={type.label}>{type.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Country */}
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-2">Country</label>
            <select
              value={filters.selectedCountry || ''}
              onChange={(e) => onFilterChange({ ...filters, selectedCountry: e.target.value })}
              className="w-full px-3 py-2.5 border-2 border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316] text-sm"
            >
              <option value="">All Countries</option>
              <option value="Malaysia">Malaysia</option>
              <option value="USA">USA</option>
              <option value="UK">UK</option>
              <option value="Singapore">Singapore</option>
            </select>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E5E7EB] px-5 py-4 flex gap-3">
          <button
            onClick={handleClearAll}
            className="flex-1 px-4 py-3 bg-white border-2 border-[#E5E7EB] text-[#111827] font-semibold rounded-xl hover:bg-[#F8FAFC] hover:border-[#F97316] transition-all text-sm"
          >
            Reset
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] text-white font-semibold rounded-xl hover:shadow-lg transition-all text-sm"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListingFilters;
